import { SongwriterBlock, SongwriterBlockType, uuid } from "../types/songwriter.types";
import {
  generateBlockPositionWithAutoRebalance,
  assignInitialPositions,
  comparePositions,
} from "../../service/fractional-indexing";

export { assignInitialPositions, comparePositions };

export function getNextBlockType(currentType: SongwriterBlockType): SongwriterBlockType {
  switch (currentType) {
    case "SONG_NAME":
      return "SONG_TYPE";
    case "SONG_TYPE":
      return "SONG_BEAT";
    case "SONG_BEAT":
      return "SONG_LYRICS";
    case "SONG_LYRICS":
      return "SONG_LYRICS"; // Pressing Enter creates consecutive lyrics
    default:
      return "SONG_LYRICS";
  }
}

export function createNewBlock(type: SongwriterBlockType): SongwriterBlock {
  const newBlock: SongwriterBlock = { id: uuid(), type, text: "" };
  if (type === "SONG_BEAT") {
    newBlock.text = "[BEAT: ]";
  }
  return newBlock;
}

export function addBlockAfter(
  blocks: SongwriterBlock[],
  currentBlockId: string,
): { newBlocks: SongwriterBlock[]; newBlockId: string } {
  const currentIndex = blocks.findIndex((b) => b.id === currentBlockId);
  if (currentIndex === -1) return { newBlocks: blocks, newBlockId: "" };

  let nextType = getNextBlockType(blocks[currentIndex].type);

  // Enforce "used only once" rule: Bypass creating duplicates on Enter keypresses
  if (nextType === "SONG_NAME" && blocks.some((b) => b.type === "SONG_NAME")) {
    nextType = "SONG_TYPE";
  }
  if (nextType === "SONG_TYPE" && blocks.some((b) => b.type === "SONG_TYPE")) {
    nextType = "SONG_BEAT";
  }

  const newBlock = createNewBlock(nextType);
  const currentBlock = blocks[currentIndex];
  const nextBlock = blocks[currentIndex + 1] || null;

  // Utilize the Base62 lexicographical indexing engine with automatic list self-healing rebalancing
  const { position, rebalancedBlocks } = generateBlockPositionWithAutoRebalance(
    blocks,
    currentBlock.position || null,
    (nextBlock && nextBlock.position) || null,
    newBlock.id,
    12 // String length rebalance threshold
  );
  newBlock.position = position;

  let newBlocks: SongwriterBlock[];
  if (rebalancedBlocks) {
    newBlocks = rebalancedBlocks.map((b) =>
      b.id === newBlock.id ? { ...newBlock, position: b.position } : b
    ) as any;
  } else {
    newBlocks = [
      ...blocks.slice(0, currentIndex + 1),
      newBlock,
      ...blocks.slice(currentIndex + 1),
    ];
  }

  return { newBlocks, newBlockId: newBlock.id };
}

export function deleteBlock(
  blocks: SongwriterBlock[],
  blockIdToDelete: string,
): { newBlocks: SongwriterBlock[]; nextFocusedId: string | null } {
  const blockToDelete = blocks.find((b) => b.id === blockIdToDelete);
  if (blockToDelete?.type === "SONG_NAME" || blockToDelete?.type === "SONG_TYPE") {
    return { newBlocks: blocks, nextFocusedId: null };
  }

  if (blocks.length <= 1) {
    return { newBlocks: blocks, nextFocusedId: blocks[0]?.id || null };
  }

  const index = blocks.findIndex((b) => b.id === blockIdToDelete);
  if (index === -1) return { newBlocks: blocks, nextFocusedId: null };

  const newBlocks = [...blocks];
  newBlocks.splice(index, 1);

  const nextFocusedId = newBlocks[index]?.id || newBlocks[index - 1]?.id || null;
  return { newBlocks, nextFocusedId };
}

export function updateBlock(
  blocks: SongwriterBlock[],
  id: string,
  text: string,
): SongwriterBlock[] {
  return blocks.map((b) => (b.id === id ? { ...b, text } : b));
}

export function changeBlockType(
  blocks: SongwriterBlock[],
  id: string,
  newType: SongwriterBlockType,
): SongwriterBlock[] {
  const currentIndex = blocks.findIndex((b) => b.id === id);
  if (currentIndex === -1) return blocks;

  // Enforce "used only once" rule: Prevent conversion to duplicate SONG_NAME or SONG_TYPE block
  if (newType === "SONG_NAME" && blocks.some((b) => b.id !== id && b.type === "SONG_NAME")) {
    return blocks;
  }
  if (newType === "SONG_TYPE" && blocks.some((b) => b.id !== id && b.type === "SONG_TYPE")) {
    return blocks;
  }

  const currentBlock = blocks[currentIndex];
  const newBlock = createNewBlock(newType);

  // Preserve text while transitioning block types
  let cleanText = currentBlock.text;
  if (currentBlock.type === "SONG_BEAT") {
    cleanText = cleanText.replace(/[\[\]]/g, "").replace(/^BEAT:\s*/i, "").trim();
  }
  
  if (newType === "SONG_BEAT") {
    newBlock.text = `[BEAT: ${cleanText.toUpperCase()}]`;
  } else {
    newBlock.text = cleanText;
  }

  return blocks.map((b) => (b.id === id ? { ...newBlock, id: b.id, position: b.position } : b));
}
